import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { fetchUserOrders } from '../lib/orders';
import './Orders.css';

function Orders() {
  const { user } = useAuth();
  const { formatMoney } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const placedOrderId = searchParams.get('placed');

  useEffect(() => {
    async function loadOrders() {
      try {
        const data = await fetchUserOrders(user.id);
        setOrders(data);
      } catch {
        setError('Could not load orders. Please try again shortly.');
      } finally {
        setLoading(false);
      }
    }

    loadOrders();
  }, [user.id]);

  const dismissConfirmation = () => {
    searchParams.delete('placed');
    setSearchParams(searchParams);
  };

  return (
    <div className="orders-page">
      <header className="orders-header">
        <p className="store-eyebrow">Account</p>
        <h1 className="store-title">Orders</h1>
        <p className="store-lede">Signed in as {user.email}</p>
      </header>

      {placedOrderId && (
        <div className="store-notice" role="status">
          <p>Order placed</p>
          <p>
            Your order has been saved
            {placedOrderId ? ` · #${placedOrderId.slice(0, 8)}` : ''}.
          </p>
          <button type="button" className="store-notice-dismiss" onClick={dismissConfirmation}>
            Dismiss
          </button>
        </div>
      )}

      {loading && <p className="store-status">Loading orders…</p>}
      {error && <p className="store-error">{error}</p>}

      {!loading && !error && orders.length === 0 && (
        <div className="orders-empty">
          <p className="store-lede">You haven’t placed any orders yet.</p>
          <Link to="/shop" className="store-btn">
            Browse the shop
          </Link>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="orders-list">
          {orders.map(order => (
            <article key={order.id} className="orders-line">
              <div className="orders-line-head">
                <div>
                  <h2 className="orders-line-title">Order #{order.id.slice(0, 8)}</h2>
                  <p className="orders-line-meta">
                    {new Date(order.created_at).toLocaleDateString()} · {order.status}
                  </p>
                </div>
                <p className="orders-line-total">{formatMoney(order.total)}</p>
              </div>

              <ul className="orders-items">
                {order.order_items?.map(item => (
                  <li key={item.id}>
                    <span>
                      {item.product_name}
                      {item.size_label ? ` · ${item.size_label}` : ''} × {item.quantity}
                    </span>
                    <span>{formatMoney(Number(item.price) * item.quantity)}</span>
                  </li>
                ))}
              </ul>

              <p className="orders-ship">
                Ships to {order.shipping_name}, {order.shipping_address},{' '}
                {order.shipping_city} {order.shipping_zip}
              </p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export default Orders;
